import React, { useState } from 'react';
import { X, Copy, CheckCircle, Wallet, Shield, Smartphone } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import LoadingDots from './LoadingDots';

const CRYPTO_OPTIONS = [
  { symbol: 'usdttrc20', name: 'USDT', network: 'TRC20', label: 'USDT', color: '#26A17B', icon: '₮' },
  { symbol: 'trx', name: 'TRON', network: 'TRC20', label: 'TRX', color: '#FF0013', icon: <img src="https://cryptologos.cc/logos/tron-trx-logo.svg" alt="TRX" style={{ width: 28, height: 28 }} /> },
  { symbol: 'btc', name: 'Bitcoin', network: 'BTC', label: 'BTC', color: '#F7931A', icon: '₿' },
  { symbol: 'eth', name: 'Ethereum', network: 'ERC20', label: 'ETH', color: '#627EEA', icon: <img src="https://cryptologos.cc/logos/ethereum-eth-logo.svg" alt="ETH" style={{ width: 28, height: 28 }} /> },
  { symbol: 'usdc', name: 'USD Coin', network: 'ERC20', label: 'USDC', color: '#2775CA', icon: '◎' },
  { symbol: 'ltc', name: 'Litecoin', network: 'LTC', label: 'LTC', color: '#345D9D', icon: 'Ł' },
];

export default function CryptoModal({
  isOpen, onClose, plan, planKey, planName, billingCycle, onPaymentComplete, onCardPayment, existingPayment = null,
}) {
  const [activeTab, setActiveTab] = useState('card'); // 'card' | 'crypto'
  const [selectedCrypto, setSelectedCrypto] = useState('usdttrc20');
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedCrypto('usdttrc20');
      setLoading(false);
      setPayment(existingPayment);
      setCopied(false);
      setCardLoading(false);
    }
  }, [isOpen, existingPayment]);

  const handleCreatePayment = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/billing/pay-usdt', {
        plan: planKey || (typeof plan === 'string' ? plan : plan?.key),
        billing_cycle: billingCycle,
        pay_currency: selectedCrypto,
      });
      setPayment(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAddress = () => {
    if (payment?.pay_address) {
      navigator.clipboard.writeText(payment.pay_address);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerifyPayment = async () => {
    try {
      const res = await api.get(`/api/billing/payment-status/${payment.payment_id}`);
      if (res.data.status === 'finished') {
        toast.success('Payment confirmed! Your plan has been upgraded.');
        onClose();
        if (onPaymentComplete) onPaymentComplete();
      } else {
        toast.info('Payment is still pending. Please wait for network confirmations.');
      }
    } catch {
      toast.error('Failed to verify payment status.');
    }
  };

  if (!isOpen) return null;

  const isFinished = payment?.payment_status === 'finished';
  const amount = payment?.amount_usd?.toFixed(2) || '0.00';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    }} onClick={cardLoading ? undefined : onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-secondary)',
        borderRadius: 20, width: '90%', maxWidth: 520, maxHeight: '90vh',
        overflow: 'hidden', boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border-primary)',
      }}>
        {/* Header with Tabs */}
        <div style={{
          background: 'linear-gradient(135deg, #0A3D91, #1E5BBE)',
          padding: '20px 28px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 }}>
              {payment ? 'Send Payment' : 'Choose Your Payment Method'}
            </h2>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none',
              borderRadius: 8, width: 32, height: 32, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
            }}>
              <X size={18} />
            </button>
          </div>
          <p style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 12px', display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'center' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{planName}</span>
            <span style={{ color: '#FFFFFF', fontSize: 26 }}>${billingCycle === 'yearly' ? plan?.price_yearly : plan?.price_monthly}</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>/{billingCycle === 'yearly' ? 'Year' : 'Month'}</span>
          </p>
          {/* Tabs - hide when payment is active */}
          {!payment && (
          <div style={{ display: 'flex', gap: 0 }}>
            <button onClick={() => setActiveTab('card')} style={{
              flex: 1, padding: '12px 0', border: 'none',
              background: activeTab === 'card' ? 'var(--bg-secondary)' : 'transparent',
              color: activeTab === 'card' ? 'var(--text-primary)' : '#FFFFFF',
              fontWeight: 600, fontSize: 12, cursor: 'pointer',
              borderRadius: '12px 12px 0 0', fontFamily: 'inherit',
              transition: 'none',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                Credit/Debit Card
              </span>
            </button>
            <button onClick={() => setActiveTab('crypto')} style={{
              flex: 1, padding: '12px 0', border: 'none',
              background: activeTab === 'crypto' ? 'var(--bg-secondary)' : 'transparent',
              color: activeTab === 'crypto' ? 'var(--text-primary)' : '#FFFFFF',
              fontWeight: 600, fontSize: 12, cursor: 'pointer',
              borderRadius: '12px 12px 0 0', fontFamily: 'inherit',
              transition: 'none',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 100 4h4a2 2 0 110 4H8"/><line x1="12" y1="18" x2="12" y2="22"/></svg>
                Crypto Payment
              </span>
            </button>
          </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>
          {activeTab === 'card' && !payment ? (
            /* Credit/Debit Card tab content */
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              {/* Card brand icons */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, marginBottom: 24,
              }}>
                {/* Visa */}
                <img loading="eager" src="https://cdn.visa.com/v2/assets/images/logos/visa/blue/logo.png" alt="Visa" style={{ height: 11 }} />
                {/* Mastercard */}
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" style={{ height: 11 }} />
                {/* Verve */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 15, padding: '0 3px', borderRadius: 4,
                  background: '#ED1C24',
                }}>
                  <span style={{ color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: 0.5 }}>VERVE</span>
                </div>
                {/* Google Pay */}
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/f2/Google_Pay_Logo.svg" alt="Google Pay" style={{ height: 11 }} />
                {/* Apple Pay */}
                <img src="https://upload.wikimedia.org/wikipedia/commons/b/b0/Apple_Pay_logo.svg" alt="Apple Pay" style={{ height: 11 }} />
                {/* Discover */}
                <img src="https://upload.wikimedia.org/wikipedia/commons/5/57/Discover_Card_logo.svg" alt="Discover" style={{ height: 11 }} />
                {/* American Express */}
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg" alt="American Express" style={{ height: 11 }} />
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 4, lineHeight: 1.6 }}>
                Credit/Debit Cards & Bank Transfers
              </p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 24 }}>
                Secure payment processed instantly
              </p>
              <button onClick={() => { 
                setCardLoading(true); 
                if (onCardPayment) {
                  onCardPayment();
                  // Close modal after starting payment
                  setTimeout(() => onClose(), 300);
                }
              }} disabled={cardLoading} style={{
                width: '100%', maxWidth: 300, height: 52, borderRadius: 14,
                border: 'none', cursor: cardLoading ? 'wait' : 'pointer',
                background: 'linear-gradient(135deg, var(--accent-amber), var(--accent-amber-hover))',
                color: '#fff', fontSize: 15, fontWeight: 700,
                fontFamily: 'inherit', opacity: cardLoading ? 0.7 : 1,
              }}>
                {cardLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'loadingDot 1.4s ease-in-out 0s infinite' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'loadingDot 1.4s ease-in-out 0.2s infinite' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'loadingDot 1.4s ease-in-out 0.4s infinite' }} />
                  </span>
                ) : (
                  'Continue to Payment'
                )}
              </button>
            </div>
          ) : activeTab === 'crypto' && !payment ? (
            <>
              {/* Crypto grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10, marginBottom: 24,
              }}>
                {CRYPTO_OPTIONS.map((crypto) => (
                  <button
                    key={crypto.symbol}
                    onClick={() => setSelectedCrypto(crypto.symbol)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      gap: 8, padding: '16px 12px', borderRadius: 14,
                      border: selectedCrypto === crypto.symbol
                        ? `2px solid ${crypto.color}`
                        : '1px solid var(--border-primary)',
                      background: selectedCrypto === crypto.symbol
                        ? `${crypto.color}10`
                        : 'var(--bg-tertiary)',
                      cursor: 'pointer', transition: 'none',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                      if (selectedCrypto !== crypto.symbol) {
                        e.currentTarget.style.borderColor = crypto.color;
                        e.currentTarget.style.background = `${crypto.color}08`;
                      }
                    }}
                    onMouseLeave={e => {
                      if (selectedCrypto !== crypto.symbol) {
                        e.currentTarget.style.borderColor = 'var(--border-primary)';
                        e.currentTarget.style.background = 'var(--bg-tertiary)';
                      }
                    }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: `${crypto.color}20`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, fontWeight: 700, color: crypto.color,
                    }}>
                      {crypto.icon}
                    </div>
                    <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600 }}>
                      {crypto.label}
                    </span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 500 }}>
                      {crypto.network}
                    </span>
                  </button>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={handleCreatePayment}
                disabled={loading}
                style={{
                  width: '100%', height: 52, borderRadius: 14,
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  background: 'linear-gradient(135deg, var(--accent-amber), var(--accent-amber-hover))',
                  color: '#fff', fontSize: 15, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, fontFamily: 'inherit',
                  opacity: loading ? 0.7 : 1,
                  transition: 'none',
                }}
              >
                {loading ? <LoadingDots /> : (
                  <>
                    <Wallet size={18} />
                    Continue to Payment
                  </>
                )}
              </button>

              {/* Security badges */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 16, marginTop: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-tertiary)', fontSize: 11 }}>
                  <Shield size={14} color="var(--success)" /> Secure
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-tertiary)', fontSize: 11 }}>
                  <Smartphone size={14} /> Mobile Ready
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Amount display */}
              <div style={{
                textAlign: 'center', padding: '20px 0',
                borderBottom: '1px solid var(--border-primary)',
                marginBottom: 20,
              }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>
                  Send exactly
                </div>
                <div style={{ color: 'var(--text-primary)', fontSize: 28, fontWeight: 700 }}>
                  {payment.pay_amount} <span style={{ fontSize: 16, color: 'var(--text-tertiary)' }}>
                    {(() => {
                      const cur = payment.pay_currency?.toUpperCase() || '';
                      if (cur === 'USDTTRC20') return 'USDT';
                      if (cur === 'USDTBEP20') return 'USDT';
                      return cur;
                    })()}
                  </span>
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 4 }}>
                  {(() => {
                    const cur = payment.pay_currency?.toUpperCase() || '';
                    const map = {
                      'USDTTRC20': 'Tether USD · TRC20 Network',
                      'USDTBEP20': 'Tether USD · BEP20 Network',
                      'BTC': 'Bitcoin · BTC Network',
                      'ETH': 'Ethereum · ERC20 Network',
                      'USDC': 'USD Coin · ERC20 Network',
                      'LTC': 'Litecoin · LTC Network',
                      'TRX': 'TRON · TRC20 Network',
                    };
                    return map[cur] || cur;
                  })()}
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 2 }}>
                  ≈ ${amount} USD
                </div>
              </div>

              {/* Address */}
              <label style={{
                display: 'block', color: 'var(--text-secondary)', fontSize: 12,
                fontWeight: 600, marginBottom: 8,
              }}>
                Wallet Address
              </label>
              <div style={{
                padding: '14px 16px', background: 'var(--bg-tertiary)',
                borderRadius: 12, border: '1px solid var(--border-primary)',
                wordBreak: 'break-all', marginBottom: 16,
              }}>
                <code style={{
                  color: 'var(--text-primary)', fontSize: 13,
                  fontFamily: 'SF Mono, Fira Code, monospace',
                  lineHeight: 1.6,
                }}>
                  {payment.pay_address}
                </code>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={handleCopyAddress} style={{
                  width: '100%', padding: 14, borderRadius: 12,
                  border: copied ? '1px solid var(--success)' : '1px solid var(--border-primary)',
                  background: copied ? 'var(--success-bg)' : 'transparent',
                  color: copied ? 'var(--success)' : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8, transition: 'none',
                }}>
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {copied ? 'Address Copied!' : 'Copy Address'}
                </button>

                <button onClick={handleVerifyPayment} style={{
                  width: '100%', height: 52, borderRadius: 14,
                  border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, var(--accent-amber), var(--accent-amber-hover))',
                  color: '#fff', fontSize: 15, fontWeight: 700,
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8,
                }}>
                  <CheckCircle size={18} />
                  I've Sent the Payment
                </button>
              </div>

              {/* Expiry warning */}
              {payment.expires_at && (
                <p style={{
                  color: 'var(--text-tertiary)', fontSize: 11,
                  textAlign: 'center', marginTop: 16,
                }}>
                  ⏰ This address expires in 24 hours
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
